import { Company, Project, ProjectStage } from '../components/projects-management/types';
import type { ProjectMetadataField } from '../components/projects-management/types/metadata';
import type { MyProjectAssociationsPayload } from '../hooks/useMyProjectAssociations';
import { DedupedGet, DedupedUrlCache } from './dedupedFetch';
import { dashboardDataService } from './dashboardDataService';
import { projectRoiService } from './projectRoiService';

/**
 * Small dedupe + short-TTL caching layer for the list endpoints that power
 * the Projects page (`/projects`, `/employees`, `/companies`,
 * `/project-stages`).
 *
 * The page tends to hit each of these exactly once per visit, but without
 * coordination we see two identical requests in several situations:
 *
 *   1. React StrictMode deliberately double-invokes effects in dev, so the
 *      initial load effect fires twice.
 *   2. Production transitions (e.g. auth-state loading flipping false) can
 *      transiently remount `ProjectsManagement`, re-firing the same effect.
 *
 * The shared primitives in {@link ./dedupedFetch} solve both at the network
 * layer: concurrent callers share one in-flight promise and subsequent
 * callers read from a 60s TTL cache until `clearProjectsDataCache()` runs.
 */

// `employees` is typed `unknown` because the endpoint returns one of several
// envelope shapes that `useProjectsData.fetchEmployees` already normalises.
export const projectsDataService = {
  projects: new DedupedGet<Project[]>('/projects'),
  employees: new DedupedGet<unknown>('/employees'),
  companies: new DedupedGet<Company[]>('/companies'),
  projectStages: new DedupedGet<ProjectStage[]>('/project-stages'),
  // The metadata-field catalogue is read by four independent components
  // (`ScreenProjects`, `ProjectsTable`, `CreateProjectDialog` and the Health
  // tab's `useProjectHealthForm`). Each mounts its own `useProjectMetadataFields`,
  // so before this entry a single visit to `/projects` issued the same GET three
  // times over — the one case here that is a real duplicate rather than
  // StrictMode's dev-only double-invoke.
  //
  // NOTE the `/api` prefix: the cache keys on the raw URL string, and this
  // endpoint's hook has always sent the prefixed form. The four entries above
  // send the unprefixed form. `buildApiUrl` normalises both to the same
  // request, so the forms must not be mixed within one entry or callers
  // silently stop sharing a slot.
  projectMetadataFields: new DedupedGet<ProjectMetadataField[]>(
    '/api/project-metadata-fields',
  ),
  // URL-keyed: one slot per setting key. Read by `useSystemSetting`, which the
  // Project Detail page and its Health tab both mount for the same key.
  systemSettings: new DedupedUrlCache<{ value: string }>(),
  // Three independent readers — `useProjectTypeOptions`, `useProjectForm` and
  // the project reports tab — and no slot until now, so this fired on every
  // Projects load, every Overview visit and every Create-Project dialog open.
  // The Settings → Types section reads `?includeArchived=true`, a different URL
  // that deliberately does not share this slot.
  projectTypes: new DedupedGet<unknown>('/api/project-types'),
  // The "my projects" association set, read by `useMyProjectAssociations` on
  // both the Projects page and the Project Timeline. One mount site per page,
  // so the doubling seen in dev is StrictMode's — but this is here for reason 2
  // above, which is not dev-only: an auth-state flip or a transient remount
  // re-fires the effect in production too.
  //
  // Unprefixed `/projects/mine`, matching the four entries at the top and the
  // hook's own call. Per the NOTE above, mixing the two forms within one entry
  // silently splits the cache slot.
  myProjectAssociations: new DedupedGet<MyProjectAssociationsPayload>('/projects/mine'),
};

/**
 * Clear every cache managed by this service. Wired up from
 * `useProjectsData.refreshData` so an explicit refresh always hits the wire.
 */
export function clearProjectsDataCache(): void {
  projectsDataService.projects.clear();
  projectsDataService.employees.clear();
  projectsDataService.companies.clear();
  projectsDataService.projectStages.clear();
  // Every entry, including the two below — this is what `useProjectsData.refreshData`
  // calls, and the metadata catalogue drives the list's metadata *columns* and
  // their filter options. Serving those from the TTL would make the one gesture
  // a user has for "give me current data" quietly not do that.
  clearProjectMetadataCache();
  clearProjectTypesCache();
  // The association set is derived from allocations and project metadata, both
  // of which a refresh may have changed, so "give me current data" must not
  // hand back a TTL-cached "my projects".
  projectsDataService.myProjectAssociations.clear();
}

/**
 * Clear the metadata-field catalogue and the system-setting slots.
 *
 * Called from every mutation in the Settings → Custom fields section
 * (`useProjectMetadataFieldsAdmin`). Without it someone who adds, edits,
 * archives or reorders a field would keep seeing the old catalogue on the
 * Projects list for up to the 60s TTL — and the section reads a *different* URL
 * (`?includeArchived=true`), so its own refetch does not refresh this slot.
 */
export function clearProjectMetadataCache(): void {
  projectsDataService.projectMetadataFields.clear();
  projectsDataService.systemSettings.clear();
}

/**
 * Clear the project-type catalogue. Called from every mutation in the
 * Settings → Types section — its own list reads `?includeArchived=true`, a
 * different URL, so its refetch does not refresh the slot the rest of the app
 * reads. The projects slot goes too, because each project embeds its type's
 * name.
 */
export function clearProjectTypesCache(): void {
  projectsDataService.projectTypes.clear();
  projectsDataService.projects.clear();
}

/**
 * Clear every copy of the stage list, and the projects that embed a stage's
 * name and colour. Called from every mutation in the Settings → Stages section,
 * whose own list reads `?includeInactive=true` and so refreshes none of these:
 * the Projects page's stages and projects, and the Dashboard's
 * all-stages read.
 */
export function clearProjectStagesCache(): void {
  projectsDataService.projectStages.clear();
  projectsDataService.projects.clear();
  dashboardDataService.projectStagesAll.clear();
}

/**
 * Clear what a rate-card change re-prices on the Projects tab: every project's
 * ROI, and the companies slot, which carries each company's rate-card count.
 * Called from every rate-card and line mutation in Projects → Settings → Rate
 * cards, whose own reads refresh neither.
 */
export function clearRateCardDependentCaches(): void {
  projectRoiService.clearCache();
  projectsDataService.companies.clear();
}

// Re-export for callers and tests that used to import directly from here.
export { DedupedGet, DedupedUrlCache };
